import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, RefreshCw, Tag, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

type Stage = { id: string; name: string; color: string; position: number; is_won: boolean; is_lost: boolean };
type Lead = {
  id: string; full_name: string | null; email: string | null; phone: string | null;
  source: string; stage_id: string | null; value: number; tags: string[];
  notes: string | null; created_at: string; last_activity_at: string;
};

export default function AdminLeads() {
  const [stages, setStages] = useState<Stage[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Lead | null>(null);
  const [showNew, setShowNew] = useState(false);

  async function load() {
    setLoading(true);
    const [s, l] = await Promise.all([
      supabase.from("lead_stages").select("*").order("position"),
      supabase.from("leads").select("*").order("position").order("created_at", { ascending: false }).limit(500),
    ]);
    setStages((s.data ?? []) as Stage[]);
    setLeads((l.data ?? []) as Lead[]);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // Realtime broadcasting of lead PII was disabled for security; poll instead.
  useEffect(() => {
    const id = window.setInterval(() => { load(); }, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { window.clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  const byStage = useMemo(() => {
    const m: Record<string, Lead[]> = {};
    for (const s of stages) m[s.id] = [];
    for (const l of leads) {
      if (l.stage_id && m[l.stage_id]) m[l.stage_id].push(l);
    }
    return m;
  }, [stages, leads]);

  async function moveLead(leadId: string, stageId: string) {
    const prev = leads;
    setLeads(ls => ls.map(l => l.id === leadId ? { ...l, stage_id: stageId } : l));
    const { error } = await supabase.from("leads").update({ stage_id: stageId, last_activity_at: new Date().toISOString() }).eq("id", leadId);
    if (error) { setLeads(prev); toast.error(error.message); return; }
    const stage = stages.find(s => s.id === stageId);
    await supabase.from("lead_activities").insert({ lead_id: leadId, type: "stage_changed", body: `Moved to ${stage?.name}`, metadata: { stage_id: stageId } });
  }

  return (
    <AdminShell title="Leads (CRM)" actions={
      <>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        <Button size="sm" onClick={() => setShowNew(true)}><Plus className="w-4 h-4 mr-1" /> New Lead</Button>
      </>
    }>
      {loading ? <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {stages.map(s => (
            <div key={s.id} className="min-w-[260px] w-[260px] bg-surface border border-border"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { const id = e.dataTransfer.getData("text/lead"); if (id) moveLead(id, s.id); }}>
              <div className="px-3 py-2 border-b border-border flex items-center justify-between" style={{ borderTopColor: s.color, borderTopWidth: 3 }}>
                <span className="font-display text-sm">{s.name}</span>
                <span className="text-xs text-muted-foreground">{byStage[s.id]?.length ?? 0}</span>
              </div>
              <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
                {(byStage[s.id] ?? []).map(l => (
                  <button key={l.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/lead", l.id)}
                    onClick={() => setSelected(l)}
                    className="w-full text-left p-3 bg-panel border border-border hover:border-primary/50 cursor-move">
                    <div className="text-sm font-body font-medium truncate">{l.full_name || l.email || "(no name)"}</div>
                    {l.email && <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{l.email}</div>}
                    {l.phone && <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</div>}
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="px-1.5 py-0.5 bg-background border border-border text-muted-foreground">{l.source}</span>
                      {l.value > 0 && <span className="font-mono">${Number(l.value).toFixed(0)}</span>}
                    </div>
                    {l.tags?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {l.tags.slice(0, 3).map(t => <span key={t} className="text-[10px] bg-primary/10 text-primary px-1"><Tag className="w-2.5 h-2.5 inline mr-0.5" />{t}</span>)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && <LeadDetailDialog lead={selected} stages={stages} onClose={() => setSelected(null)} onSaved={load} />}
      {showNew && <NewLeadDialog stages={stages} onClose={() => setShowNew(false)} onCreated={load} />}
    </AdminShell>
  );
}

function LeadDetailDialog({ lead, stages, onClose, onSaved }: { lead: Lead; stages: Stage[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(lead);
  const [acts, setActs] = useState<any[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.from("lead_activities").select("*").eq("lead_id", lead.id).order("created_at", { ascending: false })
      .then(({ data }) => setActs(data ?? []));
  }, [lead.id]);

  async function save() {
    setBusy(true);
    const { error } = await supabase.from("leads").update({
      full_name: form.full_name, email: form.email, phone: form.phone,
      stage_id: form.stage_id, value: form.value, notes: form.notes,
      tags: form.tags, last_activity_at: new Date().toISOString(),
    }).eq("id", lead.id);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Saved");
    onSaved();
    onClose();
  }

  async function addNote() {
    if (!note.trim()) return;
    await supabase.from("lead_activities").insert({ lead_id: lead.id, type: "note", body: note.trim() });
    setNote("");
    const { data } = await supabase.from("lead_activities").select("*").eq("lead_id", lead.id).order("created_at", { ascending: false });
    setActs(data ?? []);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span>{lead.full_name || lead.email || "Lead"}</span>
            <a href={`/admin/leads/${lead.id}/timeline`} className="text-xs underline font-normal">Full timeline →</a>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.full_name ?? ""} onChange={e => setForm({ ...form, full_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email ?? ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone ?? ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Stage</Label>
              <Select value={form.stage_id ?? ""} onValueChange={(v) => setForm({ ...form, stage_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Value (USD)</Label><Input type="number" value={form.value} onChange={e => setForm({ ...form, value: Number(e.target.value) })} /></div>
            <div><Label>Tags (comma)</Label><Input value={form.tags?.join(", ") ?? ""} onChange={e => setForm({ ...form, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} /></div>
            <div><Label>Notes</Label><Textarea rows={4} value={form.notes ?? ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div>
            <Label>Activity</Label>
            <div className="border border-border bg-surface max-h-64 overflow-y-auto mt-1 mb-2">
              {acts.length === 0 ? <div className="p-3 text-xs text-muted-foreground">No activity yet.</div> : acts.map(a => (
                <div key={a.id} className="p-2 border-b border-border text-xs">
                  <div className="flex justify-between gap-2 text-muted-foreground">
                    <span className="uppercase">{a.type}</span>
                    <span>{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  {a.body && <div className="mt-1 whitespace-pre-wrap">{a.body}</div>}
                </div>
              ))}
            </div>
            <Textarea placeholder="Add a note…" rows={3} value={note} onChange={e => setNote(e.target.value)} />
            <Button size="sm" className="mt-2" onClick={addNote}>Add Note</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewLeadDialog({ stages, onClose, onCreated }: { stages: Stage[]; onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({ full_name: "", email: "", phone: "", source: "manual", stage_id: stages[0]?.id, value: 0, notes: "" });
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!f.email) return toast.error("Email required");
    setBusy(true);
    const { error } = await supabase.from("leads").insert({ ...f });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Lead created");
    onCreated();
    onClose();
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.full_name} onChange={e => setF({ ...f, full_name: e.target.value })} /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
          <div><Label>Source</Label><Input value={f.source} onChange={e => setF({ ...f, source: e.target.value })} /></div>
          <div><Label>Stage</Label>
            <Select value={f.stage_id} onValueChange={v => setF({ ...f, stage_id: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Value</Label><Input type="number" value={f.value} onChange={e => setF({ ...f, value: Number(e.target.value) })} /></div>
          <div><Label>Notes</Label><Textarea rows={3} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}